import React, { useState, useEffect } from "react";
import { getAccessToken } from "../../services/authApi";
import { fieldReader, fieldWriter, createTask } from "../../services/loanApi";
import Modal from '../shared/Modal';
import Loading from "../layout/Loading";
import { info, error as logAppInsightError } from '../../utils/logger';
import { logError } from "../../utils/logError";
import '../../common.css';

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;
const LoanChangeForm = () => {
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(null);
    const [loanId, setLoanId] = useState(null);
    const [loanNumber, setLoanNumber] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState(false);
    const [approvalDate, setApprovalDate] = useState(false);
    const [requestChangeDate, setRequestChangeDate] = useState('');
    const [requestHistory, setRequestHistory] = useState('');
    const [errorMessages, setErrorMessages] = useState({ reasons: "", dateOfChange: "", notes: "" });
    const [status, setStatus] = useState(null);
    const [userName, setUserName] = useState("");
    const [formData, setFormData] = useState({
        reasons: {
            rateLock: false,
            eligibilityChange: false,
            loanAmountChange: false,
            programChange: false,
            feeUpdates: false,
        },
        dateOfChange: new Date().toISOString().split('T')[0],
        notes: '',
    });

    useEffect(() => {
        const initialize = async () => {
            if (window.elli && window.elli.script && window.elli.script.guest) {
                window.elli.script.guest.create();

                await initializeAuthObject(); // Wait for status to load
            } else {
                console.warn("elli.script.guest is not available.");
                setLoading(false); // Only set to false after everything loads
            }

        };
        initialize();
    }, []);


    const initializeAuthObject = async () => {
        try {
            window.elli.script.getObject("loan").then(async function (loanObject) {
                loanObject.all().then(async function (loan) {
                    setLoanId(loan.id);
                    setLoanNumber(loan.loanNumber);
                    await getToken(false);
                    //Reading custom field value to show if exception is already submitted
                    const tpoAuthObject = await window.elli.script.getObject("auth");
                    const authCode = await tpoAuthObject.createAuthCode();
                    const reToken = await getAccessToken(authCode);
                    const allFieldIds = Array.from(new Set([
                        "CX.COC.APPROVAL.DATE", "CX.COC.CHANGEDATE", "CX.COC.APPROVAL.HISTORY"
                    ]));
                    const fieldResponse = await fieldReader(reToken, allFieldIds, loan.id);

                    const approval = fieldResponse["CX.COC.APPROVAL.DATE"];
                    const request = fieldResponse["CX.COC.CHANGEDATE"];

                    setApprovalDate(approval);
                    setRequestChangeDate(request);
                    setRequestHistory(fieldResponse["CX.COC.APPROVAL.HISTORY"]);
                    if (request !== "//" && approval !== "//") {
                        setStatus("approved");
                    } else if (request !== "//") {
                        setStatus("pending");
                    } else {
                        setStatus("new");
                    }

                    setLoading(false);
                });

                const tpoAuthObject = await window.elli.script.getObject("auth");
                tpoAuthObject.getUser().then(async function (user) {
                    const userData = user;
                    if (!userData) return;
                    const userFullName = `${user['firstName']} ${user['lastName']}`;
                    setUserName(userFullName);
                });
            });
        } catch (error) {
            console.error("Auth block Error:", error);
            logAppInsightError("InitializeAuthObject Error", error)
        }
    };

    const getToken = async () => {
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);
            setToken(token);
        } catch (err) {
            console.error("Token fetch failed", err);
        }
        finally {
            setLoading(false);
        }
    };

    const handleReasonChange = (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            reasons: {
                ...prev.reasons,
                [name]: checked,
            },
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };
    const validateForm = () => {
        let isValid = true;
        const errors = { reasons: "", dateOfChange: "" };

        const isAnyReasonSelected = Object.values(formData.reasons).some((val) => val);
        if (!isAnyReasonSelected) {
            errors.reasons = "Please select at least one reason.";
            isValid = false;
        }

        if (!formData.dateOfChange) {
            errors.dateOfChange = "Change request date is required.";
            isValid = false;
        }

        if (!formData.notes) {
            errors.notes = "Change notes is required";
            isValid = false;
        }

        setErrorMessages(errors);
        return isValid;
    };
    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);
        const fieldData = [
            {
                id: "CX.COC.REASON.RATELOCK",
                value: formData.reasons.rateLock ? 'Y' : 'N'
            },
            {
                id: "CX.COC.REASON.ELIGIBILITY",
                value: formData.reasons.eligibilityChange ? 'Y' : 'N'
            },
            {
                id: "CX.COC.REASON.LOANAMOUNT",
                value: formData.reasons.loanAmountChange ? 'Y' : 'N'
            },
            {
                id: "CX.COC.REASON.LOANPROGRAM",
                value: formData.reasons.programChange ? 'Y' : 'N'
            },
            {
                id: "CX.COC.REASON.FEEUPDATE",
                value: formData.reasons.feeUpdates ? 'Y' : 'N'
            },
            {
                id: "CX.COC.CHANGEDATE",
                value: formData.dateOfChange
            },
            {
                id: "CX.COC.COMMENTS",
                value: formData.notes
            },
            {
                id: "CX.COC.REQ.BY",
                value: userName
            }
        ];
        //updating the custom field values
        info("COC custom field data added", { loanid: loanId });
        try {
            await fieldWriter(token, loanId, fieldData);
        } catch (err) {
            const rawDetails = err?.response?.data?.details || err.message || "Field update failed.";
            setErrorMessage(rawDetails);
            console.error("FieldWriter error:", rawDetails);
            setTimeout(() => {
                setErrorMessage("");
            }, 3000);
            setLoading(false);
            return;
        }

        const descriptionParts = [
            `Rate Lock: ${formData.reasons.rateLock ? 'Yes' : 'No'}`,
            `Change Affecting Eligibility: ${formData.reasons.eligibilityChange ? 'Yes' : 'No'}`,
            `Loan Amount Has Changed: ${formData.reasons.loanAmountChange ? 'Yes' : 'No'}`,
            `Loan Program/Structure Change: ${formData.reasons.programChange ? 'Yes' : 'No'}`,
            `Fee Updates: ${formData.reasons.feeUpdates ? 'Yes' : 'No'}`,
            `Date of Change: ${formData.dateOfChange}`,
            `Notes: ${formData.notes || 'N/A'}`
        ];
        const taskDescription = descriptionParts.join(" | ");
        //For creating a standalone task
        const tpoAuthObject = await window.elli.script.getObject("auth");
        tpoAuthObject.getUser().then(async function (user) {
            const authCode = await tpoAuthObject.createAuthCode();
            const url = `${API_DOMAIN}/api/support/create-task`;
            try {
                const formDataWithAuth = {
                    authCode: authCode,
                    loanId: loanId,
                    TaskName: `Broker Change Request - ${loanNumber}`,
                    TaskDescription: taskDescription
                };
                const response = await fetch(url, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(formDataWithAuth),
                });

                const result = await response.json();

                if (response.ok) {
                    setSuccessMessage("Success");
                    setRequestChangeDate(formData.dateOfChange); // Update local state
                    setStatus("pending");
                    setTimeout(() => {
                        setSuccessMessage("");
                    }, 3000);
                } else {
                    // Show server error (e.g. 403, 500, etc.)
                    setErrorMessage(result.message || `Failed with status ${response.status}`);
                }
            } catch (err) {
                setErrorMessage(`Unexpected error: ${err.message}`);
            } finally {
                setLoading(false);
                setTimeout(() => {
                    setErrorMessage("");
                    setSuccessMessage("");
                }, 3000);
            }
        });
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <>
            {successMessage && (
                <div className="fixed top-6 right-6 z-50 flex items-start gap-3 bg-green-100 border-l-4 border-green-600 text-green-800 p-4 rounded-md shadow-lg max-w-sm animate-fade-in">
                    <div className="text-2xl">✅</div>
                    <div className="flex-1">
                        <div className="font-semibold mb-1">Success</div>
                        <div className="text-sm leading-relaxed break-words">{successMessage}</div>
                    </div>
                    <button
                        onClick={() => setSuccessMessage('')}
                        className="ml-4 text-green-500 hover:text-green-700 focus:outline-none transition-colors duration-200"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            )}

            {errorMessage && (
                <div className="fixed top-6 right-6 z-50 flex items-start gap-3 bg-red-100 border-l-4 border-red-600 text-red-800 p-4 rounded-md shadow-lg max-w-sm animate-fade-in">
                    <div className="text-2xl">❌</div>
                    <div className="flex-1">
                        <div className="font-semibold mb-1">Error</div>
                        <div className="text-sm leading-relaxed break-words">{errorMessage}</div>
                    </div>
                    <button
                        onClick={() => setErrorMessage('')}
                        className="ml-4 text-red-500 hover:text-red-700 focus:outline-none transition-colors duration-200"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            )}

            <div className="space-y-1 p-25px">
                <div className="bg-[var(--white)] rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                    <h2 className="page-title">Change Request</h2>
                    {status === "approved" && (
                        <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9]">
                            <div className="section-header">Change Request Status</div>
                            <div className="bg-white rounded-lg shadow-xl w-full max-w-8xl p-4 overflow-auto">
                                <div className="flex items-start">
                                    <span className="text-green-500 mr-2 mt-0.5">✔</span>
                                    <p className="form-label mb-1">
                                        Your change request has been <strong>approved</strong>.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {status === "pending" && (
                        <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9]">
                            <div className="section-header">Change Request Status</div>
                            <div className="bg-white rounded-lg shadow-xl w-full max-w-8xl p-4 overflow-auto">
                                <div className="flex items-start">
                                    <span className="text-green-500 mr-2 mt-0.5">✓</span>
                                    <p className="form-label mb-1">
                                        You have already submitted a change request. It is pending for approval.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )} 
                    {status === "new" && (
                        <>
                            <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9]">
                                <div className="section-header">Details Of Requested Change</div>

                                <div className="bg-white rounded-lg shadow-xl w-full max-w-8xl p-4 overflow-auto">
                                    {/* Reason for Change */}
                                    <div className="mb-4">
                                        <label className="form-label mb-2 font-semibold">
                                            Reason: <span className="text-red-500">*</span>
                                        </label>
                                        <div className="space-y-2 text-sm">
                                            <label className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    name="rateLock"
                                                    checked={formData.reasons.rateLock}
                                                    onChange={handleReasonChange}
                                                    className="form-checkbox text-blue-600"
                                                />
                                                <label className="form-label mb-1 w-50 alignment-checkbox">Rate Lock (we will handle this for you)</label>
                                            </label>
                                            <label className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    name="eligibilityChange"
                                                    checked={formData.reasons.eligibilityChange}
                                                    onChange={handleReasonChange}
                                                    className="form-checkbox text-blue-600"
                                                />
                                                <label className="form-label mb-1 w-50 alignment-checkbox">Change Affecting Eligibility</label>
                                            </label>
                                            <label className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    name="loanAmountChange"
                                                    checked={formData.reasons.loanAmountChange}
                                                    onChange={handleReasonChange}
                                                    className="form-checkbox text-blue-600"
                                                />
                                                <label className="form-label mb-1 w-50 alignment-checkbox">Loan Amount Has Changed</label>
                                            </label>
                                            <label className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    name="programChange"
                                                    checked={formData.reasons.programChange}
                                                    onChange={handleReasonChange}
                                                    className="form-checkbox text-blue-600"
                                                />
                                                <label className="form-label mb-1 w-50 alignment-checkbox">Loan Program/Structure Change</label>
                                            </label>
                                            <label className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    name="feeUpdates"
                                                    checked={formData.reasons.feeUpdates}
                                                    onChange={handleReasonChange}
                                                    className="form-checkbox text-blue-600"
                                                />
                                                <label className="form-label mb-1 w-50 alignment-checkbox">Fee Updates (Title, Survey, Recording, etc.)</label>
                                            </label>
                                        </div>
                                        {errorMessages.reasons && (
                                            <p className="text-red-500 text-sm mt-1">{errorMessages.reasons}</p>
                                        )}
                                    </div>

                                    {/* Date of Change */}
                                    <div className="mb-4">
                                        <label className="form-label mb-1 font-semibold">
                                            Change Request Date: <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            name="dateOfChange"
                                            value={formData.dateOfChange}
                                            onChange={handleInputChange}
                                            className="border border-gray-300 rounded px-3 py-2"
                                        />
                                        {errorMessages.dateOfChange && (
                                            <p className="text-red-500 text-sm mt-1">{errorMessages.dateOfChange}</p>
                                        )}
                                    </div>

                                    {/* Notes */}
                                    <div className="mb-6">
                                        <label className="form-label mb-1 font-semibold">Notes:<span className="text-red-500">*</span></label>

                                        <textarea
                                            name="notes"
                                            value={formData.notes}
                                            onChange={handleInputChange}
                                            rows={4}
                                            placeholder="Enter additional details..."
                                            className="w-full border border-gray-300 rounded px-3 py-2 resize-none"
                                        />
                                        {errorMessages.notes && (
                                            <p className="text-red-500 text-sm mt-1">{errorMessages.notes}</p>
                                        )}
                                    </div>

                                    {/* Submit button */}
                                    <div className="flex justify-end">
                                        <button
                                            onClick={handleSubmit}
                                            className="btn-custom rounded text-white bg-[#2080C3] hover:text-[#E6EEF8] hover:bg-[#5DA5D9] 
                active:bg-white transition-colors duration-200 px-4 py-2"
                                        >
                                            Submit
                                        </button>
                                    </div>


                                </div>
                            </div>

                        </>
                    )}
                    {requestHistory && (
                        <div className="mb-4 ">
                            <label className="mb-1 pl-1 py-2 pt-4 pb-4 font-bold block">
                                Change Request History:
                            </label>

                            <textarea
                                name="notes"
                                value={requestHistory}
                                disabled
                                rows={6}
                                className="w-full border border-gray-300 rounded px-3 py-2 resize-none"
                            />
                        </div>
                    )}
                </div>
            </div >

        </>
    );
};

export default LoanChangeForm;